import React from 'react';

interface PokeverseIconProps {
  size?: number;
  className?: string;
}

export function PokeverseIcon({ size = 24, className = '' }: PokeverseIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Outer orbit ring */}
      <ellipse
        cx="16"
        cy="16"
        rx="14"
        ry="6"
        stroke="url(#orbit-gradient)"
        strokeWidth="1.5"
        fill="none"
        transform="rotate(-20 16 16)"
        opacity="0.6"
      />
      
      {/* Inner orbit ring */}
      <ellipse
        cx="16"
        cy="16"
        rx="10"
        ry="4"
        stroke="url(#orbit-gradient)"
        strokeWidth="1"
        fill="none"
        transform="rotate(25 16 16)"
        opacity="0.4"
      />
      
      {/* Center pokeball-style circle */}
      <circle
        cx="16"
        cy="16"
        r="7"
        fill="url(#ball-gradient)"
        stroke="white"
        strokeWidth="1.5"
      />
      
      {/* Pokeball line */}
      <line
        x1="9"
        y1="16"
        x2="23"
        y2="16"
        stroke="white"
        strokeWidth="1.5"
      />
      
      {/* Center button */}
      <circle
        cx="16"
        cy="16"
        r="2.5"
        fill="#1a1a1a"
        stroke="white"
        strokeWidth="1.5"
      />
      
      {/* Stars/sparkles */}
      <circle cx="5" cy="8" r="1" fill="#FFD700" opacity="0.9" />
      <circle cx="27" cy="10" r="0.8" fill="#FFD700" opacity="0.7" />
      <circle cx="25" cy="24" r="1.2" fill="#FFD700" opacity="0.8" />
      <circle cx="7" cy="22" r="0.6" fill="#FFD700" opacity="0.6" />
      <circle cx="3" cy="16" r="0.5" fill="#FFD700" opacity="0.5" />
      <circle cx="29" cy="17" r="0.7" fill="#FFD700" opacity="0.6" />
      
      {/* Gradients */}
      <defs>
        <linearGradient id="orbit-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="50%" stopColor="#06B6D4" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
        
        <linearGradient id="ball-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#EF4444" />
          <stop offset="45%" stopColor="#EF4444" />
          <stop offset="55%" stopColor="#F5F5F5" />
          <stop offset="100%" stopColor="#F5F5F5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// Animated version with rotating orbits
export function PokeverseIconAnimated({ size = 24, className = '' }: PokeverseIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes orbit1 {
            from { transform: rotate(-20deg); }
            to { transform: rotate(340deg); }
          }
          @keyframes orbit2 {
            from { transform: rotate(25deg); }
            to { transform: rotate(-335deg); }
          }
          @keyframes twinkle {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
          .orbit1 {
            transform-origin: 16px 16px;
            animation: orbit1 8s linear infinite;
          }
          .orbit2 {
            transform-origin: 16px 16px;
            animation: orbit2 12s linear infinite;
          }
          .star {
            animation: twinkle 2s ease-in-out infinite;
          }
        `}
      </style>
      
      {/* Outer orbit ring - animated */}
      <ellipse
        className="orbit1"
        cx="16"
        cy="16"
        rx="14"
        ry="5"
        stroke="url(#orbit-gradient-anim)"
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
      />
      
      {/* Inner orbit ring - animated */}
      <ellipse
        className="orbit2"
        cx="16"
        cy="16"
        rx="10"
        ry="3.5"
        stroke="url(#orbit-gradient-anim)"
        strokeWidth="1"
        fill="none"
        opacity="0.5"
      />
      
      {/* Center pokeball-style circle */}
      <circle
        cx="16"
        cy="16"
        r="6"
        fill="url(#ball-gradient-anim)"
        stroke="white"
        strokeWidth="1.5"
      />
      
      {/* Pokeball line */}
      <line
        x1="10"
        y1="16"
        x2="22"
        y2="16"
        stroke="white"
        strokeWidth="1.5"
      />
      
      {/* Center button */}
      <circle
        cx="16"
        cy="16"
        r="2"
        fill="#1a1a1a"
        stroke="white"
        strokeWidth="1.5"
      />
      
      {/* Twinkling stars */}
      <circle className="star" cx="4" cy="7" r="1" fill="#FFD700" style={{ animationDelay: '0s' }} />
      <circle className="star" cx="28" cy="9" r="0.8" fill="#FFD700" style={{ animationDelay: '0.5s' }} />
      <circle className="star" cx="26" cy="25" r="1" fill="#FFD700" style={{ animationDelay: '1s' }} />
      <circle className="star" cx="6" cy="24" r="0.7" fill="#FFD700" style={{ animationDelay: '1.5s' }} />
      
      {/* Gradients */}
      <defs>
        <linearGradient id="orbit-gradient-anim" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A855F7" />
          <stop offset="50%" stopColor="#06B6D4" />
          <stop offset="100%" stopColor="#A855F7" />
        </linearGradient>
        
        <linearGradient id="ball-gradient-anim" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#EF4444" />
          <stop offset="45%" stopColor="#EF4444" />
          <stop offset="55%" stopColor="#FAFAFA" />
          <stop offset="100%" stopColor="#FAFAFA" />
        </linearGradient>
      </defs>
    </svg>
  );
}

